"use client";

import { publishEventToSDK } from "@repo/sdk/publisher";
import {
  STATSIG_EVENTS,
  useRoutePathname,
  useStatsigClient,
  type RegisteredStatsigEvent,
} from "@repo/statsig";
import { resolveImageSource } from "@repo/ui/helpers";
import check from "@repo/ui/resources/fas-fa-check.svg";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import { useRouter } from "next/navigation";
import React, { useEffect, useMemo } from "react";

// Components
import { BodyText, HeadlineText } from "src/components/KinsaTypography";
import { FeatureSeparator } from "../../components";
import { BillingToggle } from "./billing-toggle";
import { ConditionsCarousel } from "./conditions-carousel";
import { ConversionDrawer } from "./conversion-drawer";
import { HowTrialWorks } from "./how-trial-works";
import { MeetDoctors } from "./meet-doctors";
import { PaywallFooter } from "./paywall-footer";
import { ProductTabs } from "./product-tabs";

// Hooks
import { useButtonPendingAction } from "src/lib/use-button-pending-action";
import { useFreeTrialExperiment } from "src/lib/use-free-trial-experiment";
import usePaywallProductTitles from "../hooks/use-paywall-product-titles";
import type { BillingPeriod } from "../hooks/use-paywall-state";
import { usePaywallState } from "../hooks/use-paywall-state";

// Images
import xcloseIcon from "public/icons/XCloseIcon.svg";
import kinsaHeaderWhiteImage from "public/kinsa-header-white.svg";
import kidsCareImage from "./illustrations/KidsCareImage.png";
import unlimitedRxImage from "./illustrations/UnlimitedRxImage.png";

// Constants
import { Routes } from "src/lib/routes";
import {
  ANALYTICS_EVENTS,
  ANIMATION,
  COLORS,
  GRADIENTS,
  MISC,
  SIZES,
  Z_INDEX,
} from "../constants";

/**
 * Helper function to filter out hidden value props
 */
function filterValueProps(values: string[]): string[] {
  return values.filter((value) => value !== MISC.hiddenValue);
}

/**
 * Helper function to create product content object
 */
function createProductContent(
  title: string,
  subtitle: string,
  values: string[]
) {
  return {
    title,
    subtitle,
    features: filterValueProps(values),
  };
}

export interface ProductTitles {
  RxProductTitle: string;
  PedsProductTitle: string;
  RxProductDescription: string;
  PedsProductDescription: string;
  RxValue1: string;
  RxValue2: string;
  RxValue3: string;
  RxValue4: string;
  RxValue5: string;
  RxValue6: string;
  RxValue7: string;
  PedsValue1: string;
  PedsValue2: string;
  PedsValue3: string;
  PedsValue4: string;
  PedsValue5: string;
  PedsValue6: string;
  PedsValue7: string;
  defaultBilling: BillingPeriod;
}

interface PaywallContentProps {
  productTitles?: ProductTitles;
  pedsMonthlyPrice?: number;
  pedsYearlyPrice?: number;
  fadeIn?: boolean;
  /** When provided, called instead of router.push — lets the parent handle checkout inline. */
  onStartTrial?: (productKey: string, billingPeriod: string) => void;
  /** When provided, enables dismiss UI (X close + "Not right now" link). */
  onDismiss?: () => void;
}

/**
 * Main paywall content component
 * Manages state and renders all paywall sections with improved readability
 */
export function PaywallContent({
  productTitles: productTitlesProp,
  pedsMonthlyPrice = 39,
  pedsYearlyPrice = 399,
  fadeIn,
  onStartTrial: onStartTrialProp,
  onDismiss,
}: PaywallContentProps) {
  const defaultProductTitles = usePaywallProductTitles();
  const productTitles = productTitlesProp ?? defaultProductTitles;
  const { logEvent, client } = useStatsigClient();
  const locationData = useRoutePathname();
  const router = useRouter();

  // Dynamic config: whether to show express checkout (Apple Pay / Google Pay) on the paywall footer
  const showExpressCheckout = client
    .getDynamicConfig("show_express_checkout_on_paywall_footer")
    .get("enabled", false);

  const experimentValues = productTitles;

  // Track whether the initial fade-in has completed
  const [hasFadedIn, setHasFadedIn] = React.useState(!fadeIn);

  // State for conversion drawer
  const [isConversionDrawerOpen, setIsConversionDrawerOpen] =
    React.useState(false);
  // State for navigation loading
  const { isPending: isNavigating, startAction: startNavigating } =
    useButtonPendingAction();

  // Initialize paywall state.
  // Constrain to the two products this paywall renders so `selectedProduct`
  // stays narrow when used to index productContent / GRADIENTS.
  const {
    selectedProduct,
    billingPeriod,
    setSelectedProduct,
    setBillingPeriod,
  } = usePaywallState<"unlimitedRx" | "pediatricSupport">(
    "unlimitedRx",
    experimentValues.defaultBilling
  );

  // Log paywall_viewed on mount
  React.useEffect(() => {
    logEvent({
      eventName: STATSIG_EVENTS.PAYWALL_VIEWED,
      metadata: {
        ...locationData,
        default_product: "unlimitedRx",
        default_billing: experimentValues.defaultBilling,
      },
    } as RegisteredStatsigEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally fire only on mount; logEvent and locationData are stable refs
  }, []);

  // Get free trial experiment configuration
  const { hasFreeTrial } = useFreeTrialExperiment();

  // Create simplified product content
  const productContent = useMemo(
    () => ({
      unlimitedRx: createProductContent(
        experimentValues.RxProductTitle,
        experimentValues.RxProductDescription,
        [
          experimentValues.RxValue1,
          experimentValues.RxValue2,
          experimentValues.RxValue3,
          experimentValues.RxValue4,
          experimentValues.RxValue5,
          experimentValues.RxValue6,
          experimentValues.RxValue7,
        ]
      ),
      pediatricSupport: createProductContent(
        experimentValues.PedsProductTitle,
        experimentValues.PedsProductDescription,
        [
          experimentValues.PedsValue1,
          experimentValues.PedsValue2,
          experimentValues.PedsValue3,
          experimentValues.PedsValue4,
          experimentValues.PedsValue5,
          experimentValues.PedsValue6,
          experimentValues.PedsValue7,
        ]
      ),
    }),
    [experimentValues]
  );

  const content = productContent[selectedProduct];

  // Preload images for smooth transitions
  useEffect(() => {
    const images = [unlimitedRxImage, kidsCareImage];
    const links = images.map((img) => {
      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.href = typeof img === "string" ? img : (img as { src: string }).src;
      document.head.appendChild(link);
      return link;
    });

    return () => {
      links.forEach((link) => {
        if (document.head.contains(link)) {
          document.head.removeChild(link);
        }
      });
    };
  }, []);

  // Set background color and lock horizontal scroll
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const originalStyles = {
      htmlBg: html.style.backgroundColor,
      bodyBg: body.style.backgroundColor,
      htmlOverflowX: html.style.overflowX,
      bodyOverflowX: body.style.overflowX,
    };

    html.style.backgroundColor = COLORS.background;
    body.style.backgroundColor = COLORS.background;
    html.style.overflowX = "hidden";
    body.style.overflowX = "hidden";

    return () => {
      html.style.backgroundColor = originalStyles.htmlBg;
      body.style.backgroundColor = originalStyles.bodyBg;
      html.style.overflowX = originalStyles.htmlOverflowX;
      body.style.overflowX = originalStyles.bodyOverflowX;
    };
  }, []);

  const handleClose = () => {
    // Publish SDK event
    publishEventToSDK({ topic: "dismiss", data: "success" });

    // Log analytics
    const statsigEvent: RegisteredStatsigEvent = {
      eventName: ANALYTICS_EVENTS.paywallDismissed,
      metadata: {
        ...locationData,
        product: content.title,
        billingPeriod,
      },
    };
    logEvent(statsigEvent);
    logEvent({
      eventName: STATSIG_EVENTS.UI_DISMISSED,
      value: "paywall",
      metadata: { ...locationData },
    });

    if (onDismiss) {
      onDismiss();
    }
  };

  const handleStartTrial = () => {
    // If pediatric support is selected, show the conversion drawer
    if (selectedProduct === "pediatricSupport") {
      setIsConversionDrawerOpen(true);

      // Log analytics for regular flow
      const statsigEventTwoProduct: RegisteredStatsigEvent = {
        eventName: ANALYTICS_EVENTS.twoProductContinue,
        value: "pediatric",
        metadata: {
          ...locationData,
          product: content.title,
          billingCadence: billingPeriod,
        },
      };

      logEvent(statsigEventTwoProduct);

      // Log analytics for opening conversion drawer
      const statsigEvent: RegisteredStatsigEvent = {
        eventName: "conversion_drawer_opened",
        value: `${content.title} - ${billingPeriod}`,
        metadata: {
          ...locationData,
          product: content.title,
          billingCadence: billingPeriod,
        },
      };
      logEvent(statsigEvent);
      return;
    }

    // Log analytics for regular flow
    const statsigEventTwoProduct: RegisteredStatsigEvent = {
      eventName: ANALYTICS_EVENTS.twoProductContinue,
      value: "rx",
      metadata: {
        ...locationData,
        product: content.title,
        billingCadence: billingPeriod,
      },
    };

    logEvent(statsigEventTwoProduct);

    // Log analytics for regular flow
    const statsigEvent: RegisteredStatsigEvent = {
      eventName: ANALYTICS_EVENTS.paywallStartFreeTrial,
      value: `${content.title} - ${billingPeriod}`,
      metadata: {
        ...locationData,
        product: content.title,
        billingCadence: billingPeriod,
      },
    };
    logEvent(statsigEvent);

    // Dispatch to parent if callback provided, otherwise hard-navigate
    if (onStartTrialProp) {
      onStartTrialProp(selectedProduct, billingPeriod);
    } else {
      startNavigating(() =>
        router.push(
          `${Routes.HOME}?productkey=${selectedProduct}&cadence=${billingPeriod}`
        )
      );
    }
  };

  // Handler for "Continue with Rx" button in conversion drawer
  const handleContinueWithRx = () => {
    // Close the drawer
    setIsConversionDrawerOpen(false);

    // Switch to unlimited Rx product
    setSelectedProduct("unlimitedRx");

    // Dispatch to parent if callback provided, otherwise hard-navigate
    if (onStartTrialProp) {
      onStartTrialProp("unlimitedRx", billingPeriod);
    } else {
      router.push(
        `${Routes.HOME}?productkey=unlimitedRx&cadence=${billingPeriod}`
      );
    }
  };

  return (
    <>
      {/* Fixed background */}
      <div
        className="fixed inset-0"
        style={{
          backgroundColor: COLORS.background,
          zIndex: Z_INDEX.background,
        }}
      />

      {/* Fixed header with blur - just covers safe area */}
      <div className="fixed top-0 left-0 right-0 h-[var(--felt-safe-area-top,0px)] z-[99998] backdrop-blur-[12px] bg-teal-1000/80" />

      <motion.div
        className="relative w-full min-h-screen overflow-x-hidden"
        {...(fadeIn
          ? {
              initial: { backgroundColor: COLORS.background, opacity: 0 },
              animate: { backgroundColor: COLORS.background, opacity: 1 },
              transition: { duration: 0.3, ease: "easeOut" },
              onAnimationComplete: () => setHasFadedIn(true),
            }
          : { style: { backgroundColor: COLORS.background } })}
      >
        <motion.div
          initial={fadeIn ? { opacity: 0 } : undefined}
          animate={hasFadedIn ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          {/* Close Button - Absolute position */}
          <button
            type="button"
            onClick={handleClose}
            className="absolute right-[24px] overflow-clip size-[24px] z-[9999]"
            aria-label="Close"
          >
            <Image
              src={xcloseIcon}
              alt="Close"
              width={24}
              height={24}
              className="block max-w-none size-full"
              unoptimized
            />
          </button>

          {/* Page Content */}
          <div className="px-[24px] pt-[24px] w-full relative pb-[120px]">
            {/* Kinsa Logo - Centered */}
            <div className="flex justify-center mb-[24px]">
              <Image
                src={kinsaHeaderWhiteImage}
                alt="Kinsa Logo"
                height={32}
                className="object-contain"
                priority
              />
            </div>

            {/* Product Switcher Section - Billing Toggle and Product Tabs */}
            <div
              className="flex flex-col gap-[24px] items-start w-full relative z-10"
              id="product-switcher"
            >
              <BillingToggle
                billingPeriod={billingPeriod}
                onBillingPeriodChange={setBillingPeriod}
              />
              <ProductTabs
                selectedProduct={selectedProduct}
                onProductChange={setSelectedProduct}
                rxProductTitle={experimentValues.RxProductTitle}
                pedsProductTitle={experimentValues.PedsProductTitle}
                rxProductDescription={experimentValues.RxProductDescription}
                pedsProductDescription={experimentValues.PedsProductDescription}
                billingPeriod={billingPeriod}
                pedsMonthlyPrice={pedsMonthlyPrice}
                pedsYearlyPrice={pedsYearlyPrice}
              />
            </div>

            {/* Product Image/Illustration Group - 40px below product switcher */}
            <div className="flex justify-center mt-[40px] w-full relative z-0">
              <AnimatePresence mode="wait">
                <motion.div
                  key={selectedProduct}
                  {...ANIMATION.fadeInOut}
                  {...(!hasFadedIn && {
                    initial: { opacity: 0 },
                    animate: { opacity: 0 },
                  })}
                  className="relative"
                >
                  {/* Illustration */}
                  <div
                    className="relative mx-auto"
                    style={{
                      width: `${SIZES.illustration.width}px`,
                      height: `${SIZES.illustration.height}px`,
                    }}
                  >
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                      <Image
                        alt={`${content.title} illustration`}
                        src={
                          selectedProduct === "unlimitedRx"
                            ? unlimitedRxImage
                            : kidsCareImage
                        }
                        className="absolute h-full left-0 max-w-none top-0 w-full object-contain"
                        width={SIZES.illustration.width}
                        height={SIZES.illustration.height}
                        priority
                        loading="eager"
                        sizes={`(max-width: 768px) ${SIZES.illustration.width}px, ${SIZES.illustration.width}px`}
                      />
                    </div>
                  </div>

                  {/* Gradient overlay */}
                  <div
                    className="absolute bg-gradient-to-b from-[20.69%] from-[rgba(0,30,43,0)] to-teal-1000 to-[78.901%] left-1/2 translate-x-[-50%]"
                    style={{
                      height: GRADIENTS[selectedProduct].height,
                      width: GRADIENTS[selectedProduct].width,
                      top: GRADIENTS[selectedProduct].topOffset,
                    }}
                  />
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Content Area - Product Title and Feature List */}
            <div className="flex flex-col gap-[16px] items-center w-full relative z-10 -mt-[56px]">
              {/* Product Title */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={`header-${selectedProduct}`}
                  {...ANIMATION.contentFade}
                  className="flex flex-col items-center text-white text-center w-full relative z-10"
                >
                  <HeadlineText className="text-white w-full">
                    {content.title}
                  </HeadlineText>
                </motion.div>
              </AnimatePresence>

              {/* Feature List */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={`features-${selectedProduct}`}
                  {...ANIMATION.contentFade}
                  className="flex flex-col gap-[8px] items-start w-full px-0"
                >
                  {content.features.map((feature, index) => (
                    <React.Fragment key={index}>
                      <div className="flex gap-[8px] items-start w-full">
                        <CheckIcon />
                        <BodyText className="text-slate-50 text-[16px] leading-[22px] tracking-[0.25px] flex-1">
                          {feature}
                        </BodyText>
                      </div>
                      {index < content.features.length - 1 && (
                        <FeatureSeparator />
                      )}
                    </React.Fragment>
                  ))}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* New Sections - spaced 64px apart */}
            <div className="flex flex-col gap-[64px] mt-[64px] w-full relative z-10 pb-[40px]">
              {/* How your trial works */}
              <HowTrialWorks />

              {/* Care for 30+ Conditions */}
              <ConditionsCarousel />

              {/* Meet our Top Doctors */}
              <MeetDoctors />
            </div>
          </div>

          {/* Footer */}
          <PaywallFooter
            productTitle={content.title}
            billingPeriod={billingPeriod}
            selectedProduct={selectedProduct}
            onStartTrial={handleStartTrial}
            hasFreeTrial={hasFreeTrial}
            disabled={isNavigating}
            loading={isNavigating}
            showExpressCheckout={showExpressCheckout}
            onDismiss={onDismiss ? handleClose : undefined}
          />

          {/* Conversion Drawer */}
          <ConversionDrawer
            isOpen={isConversionDrawerOpen}
            onClose={() => setIsConversionDrawerOpen(false)}
            onContinueWithRx={handleContinueWithRx}
            billingPeriod={billingPeriod}
            rxProductTitle={experimentValues.RxProductTitle}
            pedsProductTitle={experimentValues.PedsProductTitle}
          />
        </motion.div>
      </motion.div>
    </>
  );
}

/**
 * Check icon component for feature list items
 */
function CheckIcon() {
  return (
    <div className="box-border flex items-start pb-0 px-0 relative shrink-0">
      <div className="overflow-clip relative shrink-0 size-[22px] flex items-center">
        <div className="absolute inset-[17.19%_3.13%]">
          <div className="absolute inset-0">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              alt=""
              className="block max-w-none size-full"
              src={resolveImageSource(check)}
              style={{
                // CSS filter to convert to purple-700
                filter:
                  "brightness(0) saturate(100%) invert(48%) sepia(95%) saturate(2000%) hue-rotate(280deg) brightness(0.95) contrast(1.05)",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
